import React, { useEffect, useState } from "react";
import { ReactSVG } from "react-svg";
import "react-tooltip/dist/react-tooltip.css";
import {
  EducationUITypes,
  MedicationClasses,
  SignsOfBloodPressure,
  CholesterolMedicationClasses,
  BloodPressureMedicationClasses,
} from "./data";
import DrugTableUI from "./drug-table-ui";
import { useSelector } from "react-redux";
import DrugCardUI from "./drug-card-ui";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import InformationServiceModel from "../../../../common/InformationServiceModel";
import Button from "../../../../common/Button";
import InformationPage from "./information-page-ui";
import MedicationImportanceAlert from "./MedicationImportanceAlert";
import CholesterolImportanceAlert from "./CholesterolImportanceAlert";
import NewToolTip from "../../../../common/New-ToolTip";

const EducationPage = ({
  step,
  tabIndex,
  activePage,
  totalTabs,
  moveBack = () => { },
  moveForward = () => { },
  moveToNextTab = () => { },
  updateShowMedication = () => { },
  confirmEducation = () => { },
  onSelectDrugForMedicationCreate = () => { },
}) => {
  const { amrEducation } = useSelector((state) => state.clinicalService);
  const [saveDrugTableInfo, setSaveDrugTableInfo] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showBlisterPackDialog, setShowBlisterPackDialog] = useState(false);
  const [blisterPackOption, setBlisterPackOption] = useState(null);

  useEffect(() => {
    if (step?.type === EducationUITypes.DrugTable) {
      updateShowMedication(true);
      setSaveDrugTableInfo(true);
    } else {
      updateShowMedication(false);
      // Keep saveDrugTableInfo true when navigating to DrugCard to preserve amrEducation
      if (step?.type !== EducationUITypes.DrugCard) {
        setSaveDrugTableInfo(false);
      }
    }
  }, [step, updateShowMedication]);

  const currentTabName = totalTabs?.[tabIndex]?.name;
  const currentTabLower = (currentTabName || "").toLowerCase();
  const isCholesterolTab = currentTabLower.includes("cholesterol");
  const isBloodPressureTab =
    currentTabLower.includes("blood") || currentTabLower.includes("pressure");
  const chevronStrokeColor = isBloodPressureTab
    ? "var(--blood-pressure-color)"
    : isCholesterolTab
      ? "var(--cholesterol-color)"
      : "var(--reel-color)";

  const isFirstPage = activePage === 1;
  const currentTabStepCount = totalTabs?.[tabIndex]?.steps?.length || 0;
  const isLastPage = currentTabStepCount <= activePage;

  const goToNextPage = () => {
    // Smoking cessation dialog disabled - always move forward
    moveForward();
  };

  const goToNextTab = () => {
    if (totalTabs[tabIndex + 1]?.name?.toLowerCase() === "lifestyle")
      setShowInfoDialog(true);
    // else if (
    //   totalTabs[tabIndex]?.name.toLowerCase() === "general" &&
    //   patient?.preferences !== "blisterPack"
    // )
    //   setShowBlisterPackDialog(true);
    else moveToNextTab();
  };

  const getNextTabName = () => {
    if (tabIndex < totalTabs.length - 1) {
      return `Go to ${totalTabs[tabIndex + 1].label}`;
    } else {
      return "Complete Education";
    }
  };

  const handleCustomContent = (tabIndex, step) => {
    const currentTabName = totalTabs?.[tabIndex]?.name;

    // Blood Pressure content (unchanged)
    if (currentTabName === "bloodPressure") {
      switch (step) {
        case 1:
          return (
            <>
              <div className="w-full flex flex-row justify-center">
                <div className="relative flex min-w-[420px] flex-row justify-center">
                  <div className="absolute flex">
                    <ReactSVG
                      src={`/icons/bp-icon-2.svg`}
                      className="flex justify-center opacity-100"
                    />
                  </div>
                  <div
                    className={`max-w-[590px] h-fit p-5 rounded-[12px] border-[6px] mt-14 gap-[10px] border-teal-100`}
                  >
                    <div
                      className={`flex w-full flex-col max-w-[570px] justify-center items-center rounded-[12px] shadow-lg shadow-gray-400 border-REELTeal`}
                    >
                      <div
                        className={`flex w-full justify-center items-center bg-teal-75 border-4 pt-16 pb-3 rounded-t-[12px] border-teal-100`}
                      >
                        <p className="text-3xl font-semibold text-teal-300">
                          What is Blood Pressure?
                        </p>
                      </div>

                      <div
                        className={`flex w-full border-4 rounded-b-[12px] border-teal-100 py-4 px-6`}
                      >
                        <ul className="flex flex-col text-[22px] text-gray-600 font-normal gap-2">
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-1/2 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-teal-100 before:transform before:-translate-y-1/2">
                            It is the PRESSURE of the blood against the inside
                            walls of your blood vessels
                          </li>
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-1/2 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-teal-100 before:transform before:-translate-y-1/2">
                            When blood pressure is CONTROLLED, blood flows easily
                            and can supply all organs with oxygen and nutrients
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative flex min-w-[420px] flex-row justify-center pt-2 pl-36">
                  <div className={`relative flex flex-col`}>
                    <div className="absolute z-10 flex top-[-20px] left-0">
                      <ReactSVG
                        src={`/icons/bp-icon-2.svg`}
                        className="flex justify-center opacity-100"
                      />
                    </div>
                    <div className="flex flex-col w-[690px]">
                      <span className="flex font-semibold text-3xl text-teal-300 text-center justify-center items-center h-[77px] rounded-full gap-[10px] bg-teal-75 py-[10px] pr-[50px] pl-[160px]">
                        Signs of HIGH Blood Pressure
                      </span>
                      <div className="flex flex-col text-[22px] font-medium gap-[10px] pt-8 pl-24 text-gray-600">
                        {/* First row: Nausea and vomiting & Blurred vision */}
                        <div className="flex flex-row gap-4 relative">
                          <div className="absolute border-l-4 h-[120%] left-[-5%] border-teal-100"></div>
                          <div className="absolute h-1 bg-teal-100 top-[55%] left-[-5%] w-8"></div>
                          <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-teal-100 top-[45%] left-[-1%] bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-REELTeal"></span>
                          </span>
                          <div className="flex-1 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-teal-25 border-teal-100 py-3 px-6">
                              {SignsOfBloodPressure[3]}
                            </span>
                          </div>
                          <div className="flex-1 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-teal-25 border-teal-100 py-3 px-6">
                              {SignsOfBloodPressure[4]}
                            </span>
                          </div>
                        </div>
                        {/* Rest of the items */}
                        {SignsOfBloodPressure.slice(0, 3).map((x, i) => {
                          const actualIndex = i;
                          const isLast = actualIndex === 2;
                          return (
                            <div key={i} className="relative">
                              {isLast ? (
                                <div className="absolute border-l-4 border-b-4 w-[5%] h-[120%] left-[-5%] top-[-55%] border-teal-100 rounded-bl-xl"></div>
                              ) : (
                                <>
                                  <div className="absolute border-l-4 h-[120%] left-[-5%] border-teal-100"></div>
                                  <div className="absolute h-1 bg-teal-100 top-[55%] left-[-5%] w-8"></div>
                                </>
                              )}
                              <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-teal-100 top-[45%] left-[-1%] bg-white">
                                <span className="flex w-[7px] h-[7px] rounded-full bg-REELTeal"></span>
                              </span>
                              <span className="flex w-full border-4 rounded-[12px] bg-teal-25 border-teal-100 py-3 px-6">
                                {x}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        case 2:
          return (
            <>
              <div className="w-full flex flex-row justify-center">
                <div className="relative flex min-w-[420px] flex-row justify-center pt-2">
                  <div className={`relative flex flex-col`}>
                    <div className="absolute z-10 flex top-[-20px] left-0">
                      <ReactSVG
                        src={`/icons/bp-icon-2.svg`}
                        className="flex justify-center opacity-100"
                      />
                    </div>
                    <div className="flex flex-col w-[700px]">
                      <span className="flex font-semibold text-3xl text-teal-300 text-center justify-center items-center h-[77px] rounded-full gap-[10px] bg-teal-75 py-[10px] pr-[50px] pl-[160px]">
                        Blood Pressure Target
                      </span>
                      <ul className="flex flex-col text-[22px] font-medium gap-[10px] pt-8 pl-24 text-gray-600">
                        <li className="relative">
                          <div
                            class={`absolute border-l-4 h-[120%] left-[-5%] border-teal-100`}
                          ></div>
                          <div
                            class={`absolute h-1 bg-teal-100 top-[55%] left-[-5%] w-8`}
                          ></div>
                          <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-REELTeal top-[45%] left-[-1%] bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-teal-300"></span>
                          </span>
                          <span className="flex justify-center w-full text-3xl font-semibold text-gray-600 border-4 rounded-full bg-teal-25 border-teal-100 py-3 px-6">
                            <div className="space-x-6">
                              <span>Blood Pressure LESS than 130/80</span>
                            </div>
                            {/* {"< 120/80; < 130/90;  < 140/90;"} */}
                          </span>
                        </li>
                        <li className="relative">
                          <div
                            class={`absolute border-l-4 border-b-4 w-[5%] h-[120%] left-[-5%] top-[-55%] border-teal-100 rounded-bl-xl`}
                          ></div>
                          <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-teal-100 top-[45%] left-[-1%] bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-teal-300"></span>
                          </span>
                          <span id="bood-pressure-target" className="block w-full border-4 rounded-[12px] border-teal-100 py-3 px-6">
                            MONITOR blood pressure at home, RECORD results and bring to your appointments.
                            <button
                              type="button"
                              aria-label="Why monitor blood pressure at home"
                              data-tooltip-id="blood-pressure-target-tooltip"
                              className="ml-2 inline-flex h-5 w-5 cursor-pointer items-center justify-center align-[-3px]"
                            >
                              <ReactSVG
                                src="/icons/information-dark.svg"
                                wrapper="span"
                                beforeInjection={(svg) => svg.setAttribute("aria-hidden", "true")}
                                className="block h-5 w-5"
                              />
                            </button>
                            <NewToolTip
                              id="blood-pressure-target-tooltip"
                              render={() => (<div className="bg-gray-10 p-4 rounded shadow-lg max-w-md">
                                <p className="text-base text-gray-700">
                                  Your blood pressure readings help your healthcare provider make a more accurate assessment based on average readings over time, rather than a single office reading.
                                </p>
                              </div>)}
                            />
                          </span>
                        </li>
                        <li className="relative">
                          <div
                            class={`absolute border-l-4 border-b-4 w-[5%] h-[120%] left-[-5%] top-[-55%] border-teal-100 rounded-bl-xl`}
                          ></div>
                          <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-teal-100 top-[45%] left-[-1%] bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-teal-300"></span>
                          </span>
                          <span className="flex w-full items-center border-4 rounded-[12px] border-teal-100 py-3 px-6">
                            <span className="inline-flex items-center gap-2">
                              <span>
                                Check with your doctor about your blood pressure targets.
                              </span>
                            </span>
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="relative flex min-w-[420px] flex-row justify-center pt-2 pl-10">
                  <div className={`relative flex flex-col`}>
                    <div className="absolute z-10 flex top-[-20px] left-0">
                      <ReactSVG
                        src={`/icons/bp-icon-2.svg`}
                        className="flex justify-center opacity-100"
                      />
                    </div>
                    <div className="flex flex-col w-[700px]">
                      <span className="flex font-semibold text-3xl text-teal-300 text-center justify-center items-center h-[77px] rounded-full gap-[10px] bg-teal-75 py-[10px] pr-[50px] pl-[160px]">
                        Blood Pressure Numbers
                      </span>
                      <ul className="flex flex-col text-[22px] font-medium gap-[10px] pt-8 pl-24 text-gray-600">
                        <li className="relative">
                          <div
                            class={`absolute border-l-4 border-b-4 w-[5%] h-[120%] top-[-65%] left-[-5%] border-teal-100`}
                          ></div>
                          <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-teal-100 top-[45%] left-[-1%] bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-teal-300"></span>
                          </span>
                          <span className="flex w-full border-4 rounded-[12px] border-teal-100 py-3 px-6">
                            <p>
                              <b>TOP NUMBER {"—>"} Systolic Blood Pressure</b>{" "}
                              <br />
                              Measures pressure as the heart beats and pushes
                              blood into arteries
                            </p>
                          </span>
                        </li>
                        <li className="relative">
                          <div
                            class={`absolute border-l-4 border-b-4 w-[5%] h-[135%] top-[-80%] left-[-5%] border-teal-100 rounded-bl-xl`}
                          ></div>
                          <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-teal-100 top-[45%] left-[-1%] bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-teal-300"></span>
                          </span>
                          <span className="flex w-full  border-4 rounded-[12px] border-teal-100 py-3 px-6">
                            <p>
                              <b>BOTTOM NUMBER {"—>"} Diastolic Blood Pressure</b>{" "}
                              <br />
                              Measures pressure as the heart relaxes between beats
                            </p>
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        case 3:
          return null; // Hidden - will skip to next page
        default:
          break;
      }
    }

    // Cholesterol content
    if (currentTabName === "Cholesterol" || currentTabName === "cholesterol") {
      switch (step) {
        case 1:
          return (
            <>
              <div className="w-full flex flex-row justify-center">
                <div className="relative flex min-w-[420px] flex-row justify-center">
                  <div className="absolute flex">
                    <ReactSVG
                      src={`/icons/cholesterol-icon.svg`}
                      className="flex justify-center opacity-100"
                    />
                  </div>
                  <div
                    className={`max-w-[590px] h-fit p-5 rounded-[12px] border-[6px] mt-14 gap-[10px] border-green-150`}
                  >
                    <div
                      className={`flex w-full flex-col max-w-[570px] justify-center items-center rounded-[12px] shadow-lg shadow-green-150 border-green-150`}
                    >
                      <div
                        className={`flex w-full justify-center items-center bg-green-75 border-4 pt-16 pb-3 rounded-t-[12px] border-green-150`}
                      >
                        <p className="text-3xl font-semibold text-teal-300">
                          What is Cholesterol ?
                        </p>
                      </div>

                      <div
                        className={`flex w-full border-4 rounded-b-[12px] border-green-150 py-4 px-6`}
                      >
                        <ul className="flex flex-col text-[20px] text-gray-800 font-normal gap-2">
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-3 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-green-150">
                            Cholesterol is a type of fat found in your blood
                          </li>
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-3 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-green-150">
                            Your body needs some cholesterol to build cells and make hormones
                          </li>
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-3 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-green-150">
                            HIGH Cholesterol causes a build of plaque in the arteries & can reduce the flow of oxygen-rich blood to the heart.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative flex min-w-[420px] flex-row justify-center pt-2 pl-36">
                  <div className={`relative flex flex-col`}>
                    <div className="absolute z-10 flex top-[-20px] left-0">
                      <ReactSVG
                        src={`/icons/cholesterol-icon.svg`}
                        className="flex justify-center opacity-100"
                      />
                    </div>
                    <div className="flex flex-col w-[690px]">
                      <span className="flex font-semibold text-3xl text-teal-300 text-center justify-center items-center h-[77px] rounded-full gap-[10px] bg-green-75 py-[10px] pr-[50px] pl-[160px] whitespace-nowrap">
                        Warning Signs of High Cholesterol
                      </span>
                      <div className="flex flex-col text-[20px] font-medium gap-[10px] pt-8 pl-24 text-gray-800">
                        {[
                          'Sometimes there are no warning signs',
                          'Know your levels',
                          'High cholesterol is a silent killer',
                          'A blood test is the best way to check your levels',
                        ].map((text, i, arr) => (
                          <div key={i} className="relative">
                            {i === arr.length - 1 ? (
                              <div className="absolute border-l-4 border-b-4 w-[5%] h-[120%] left-[-5%] top-[-55%] border-green-150"></div>
                            ) : (
                              <div className="absolute border-l-4 h-[120%] left-[-5%] border-green-150"></div>
                            )}
                            <div className="absolute h-1 bg-green-150 top-[55%] left-[-5%] w-8"></div>
                            <span className="absolute flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-green-150 top-[45%] left-[-1%] bg-white">
                              <span className="flex w-[7px] h-[7px] rounded-full bg-green-150"></span>
                            </span>
                            <span className="flex w-full border-4 rounded-[12px] bg-green-50 border-green-150 py-3 px-6">
                              {text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        case 4:
          return (
            <div className="relative flex min-h-[490px] flex-col gap-2 p-1">
              <h1 className="h-[29px] font-bold text-2xl text-REELTeal">
                Types of Cholesterol
              </h1>
              <div className="pt-4 flex flex-row justify-between items-center gap-[20px]">
                {!isFirstPage && (
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 z-0 flex items-center justify-center">
                    <button
                      className="flex items-center justify-center w-[48px] h-[48px] -ml-6 bg-gray-10 rounded-full hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => {
                        moveBack();
                      }}
                    >
                      <ChevronLeftIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                    </button>
                  </div>
                )}
                <div className="flex w-full flex-row -ml-3">
                  <DrugTableUI
                    saveDrugTableInfo={saveDrugTableInfo}
                    classes={CholesterolMedicationClasses}
                  />
                </div>
                <div className="absolute -right-0 top-1/2 -translate-y-1/2 z-0 flex items-center justify-center">
                  <button
                    className="flex items-center justify-center w-[48px] h-[48px] bg-green-50 rounded-full hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => {
                      // If there are no selected drugs (no cards to show), open the Lifestyle dialog
                      if (
                        currentTabName?.toLowerCase() === "cholesterol" &&
                        (!amrEducation || amrEducation.length === 0)
                      ) {
                        setShowInfoDialog(true);
                      } else {
                        goToNextPage();
                      }
                    }}
                  >
                    <ChevronRightIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                  </button>
                </div>
              </div>
            </div>
          );
        case 5:
          // Show Drug cards for Cholesterol only when drugs are selected in the DrugTable (amrEducation)
          if (!amrEducation || amrEducation.length === 0) return null;

          return (
            <div className="relative flex min-h-[530px] flex-col gap-3 overflow-x-clip rounded-[12px] border-4 border-green-150 p-3">
              <div className="w-full bg-green-75 py-3 px-2 mb-0 rounded-t-[12px]">
                <h1 className="font-bold text-2xl text-teal-300">How to Treat Cholesterol</h1>
              </div>
              <>
                <div className="relative flex min-w-0 flex-1 flex-row items-center">

                  {!isFirstPage && (
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center">
                      <button
                        className="flex items-center justify-center w-[48px] h-[48px] bg-green-50 rounded-full hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => {
                          moveBack();
                        }}
                      >
                        <ChevronLeftIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                      </button>
                    </div>
                  )}

                  <div className="min-w-0 flex-1 px-16">
                    <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain scroll-smooth">
                      <div
                        className={`inline-flex min-h-[390px] flex-row flex-nowrap items-start ${amrEducation?.length === 1
                            ? 'w-full justify-center'
                            : amrEducation?.length === 2
                              ? 'w-full justify-center gap-8'
                              : amrEducation?.length >= 3
                                ? 'w-max justify-start gap-8'
                                : 'w-full justify-start pl-8'
                          }`}>
                        <DrugCardUI currentTabName={currentTabName} iconUrl={"cholesterol-rx"} headerBg={"bg-green-75"} borderClass={"border-green-150"} headerTextClass={"text-teal-300"} selectedBorderClass={"border-green-150"} />
                      </div>
                    </div>
                  </div>

                  <div className="absolute right-2 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center">
                    <button
                      className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100 text-teal-100"
                      onClick={() => {
                        !isLastPage ? goToNextPage() : goToNextTab();
                      }}
                    >
                      <ChevronRightIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                    </button>
                  </div>
                </div>
              </>
            </div>
          );
        case 3:
          // Types of Cholesterol - three cards
          const cholesterolCards = [
            {
              title: 'LDL Cholesterol',
              sub: '(Low-density lipoprotein)',
              points: [
                'Often called "bad" cholesterol.',
                'Too much LDL can build up in blood vessels and increase heart disease risk.',
                'This number should be LOWER.',
              ],
            },
            {
              title: 'HDL Cholesterol',
              sub: '(High-density lipoprotein)',
              points: [
                'Known as "good" cholesterol.',
                'HDL helps remove extra cholesterol from the blood.',
                'This number should be HIGHER.',
              ],
            },
            {
              title: 'Triglycerides',
              sub: '',
              points: [
                'Another type of fat in the blood.',
                'High levels can also increase heart disease risk.',
                'This number should be LOWER.',
              ],
            },
          ];

          return (
            <>
              <div className="w-full flex flex-row justify-center gap-16">
                <div className="flex-1 flex flex-row justify-center">
                  <div className="w-full max-w-[980px] flex flex-row justify-center items-start gap-16">
                    {cholesterolCards.map((c, idx) => (
                      <div key={idx} className="relative flex-none w-[420px]">
                        <div className="absolute -left-14 top-0">
                          <ReactSVG src="/icons/cholesterol-icon.svg" className="w-14 h-14" />
                        </div>
                        <div className="pt-6 flex flex-col h-[320px]">
                          <div className="rounded-t-[12px] bg-green-75 border-4 border-green-150 py-4 px-6 text-center h-[88px] flex items-center justify-center">
                            <div className="flex flex-col items-center gap-1">
                              <h3 className="text-2xl font-semibold text-teal-300">{c.title}</h3>
                              {c.sub && <span className="text-sm text-gray-800">{c.sub}</span>}
                            </div>
                          </div>
                          <div className="border-4 rounded-b-[12px] border-green-150 p-3 mt-0 bg-white flex-1 overflow-hidden">
                            <ul className="flex flex-col text-[20px] text-gray-800 gap-3">
                              {c.points.map((p, i) => (
                                <li key={i} className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-3 before:w-[12px] before:h-[12px] before:rounded-full before:border-4 before:border-green-150">
                                  {p}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          );
        case 2:
          return (
            <>
              <div className="w-full flex flex-row justify-center">
                <div className="relative flex min-w-[420px] flex-row justify-center">
                  <div className="absolute flex">
                    <ReactSVG
                      src={`/icons/cholesterol-icon.svg`}
                      className="flex justify-center opacity-100"
                    />
                  </div>
                  <div
                    className={`max-w-[590px] h-fit p-5 rounded-[12px] border-[6px] mt-14 gap-[10px] border-green-150`}
                  >
                    <div
                      className={`flex w-full flex-col max-w-[570px] justify-center items-center rounded-[12px] shadow-lg shadow-gray-400 border-green-150`}
                    >
                      <div
                        className={`flex w-full justify-center items-center bg-green-75 border-4 pt-16 pb-3 rounded-t-[12px] border-green-150`}
                      >
                        <p className="text-3xl font-semibold text-teal-300">
                          Cholesterol Target Levels
                        </p>
                      </div>

                      <div
                        className={`flex w-full border-4 rounded-b-[12px] border-green-150 py-4 px-6`}
                      >
                        <ul className="flex flex-col text-[20px] text-gray-800 font-normal gap-2">
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-1/2 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-green-150 before:transform before:-translate-y-1/2">
                            Generally target: under 2.0 mmol/L
                          </li>
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-1/2 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-green-150 before:transform before:-translate-y-1/2 whitespace-nowrap">
                            Goal : Lower LDL by 50% or more from baseline
                          </li>
                          <li className="relative pl-8 before:content-[''] before:absolute before:left-0 before:top-1/2 before:w-[14px] before:h-[14px] before:rounded-full before:border-4 before:border-green-150 before:transform before:-translate-y-1/2">
                            Your target Level depends on your risk factors
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="relative flex min-w-[420px] flex-row justify-center pt-2 pl-36">
                  <div className={`relative flex flex-col`}>
                    <div className="absolute z-10 flex top-[-20px] left-0">
                      <ReactSVG
                        src={`/icons/cholesterol-icon.svg`}
                        className="flex justify-center opacity-100"
                      />
                    </div>
                    <div className="flex flex-col w-[690px]">
                      <span className="flex font-semibold text-3xl text-teal-300 text-center justify-center items-center h-[77px] rounded-full gap-[10px] bg-green-75 py-[10px] pr-[50px] pl-[160px]">
                        Goals Depend on Risk Factors:
                      </span>
                      <div className="flex flex-col text-[20px] font-medium gap-[10px] pt-8 pl-24 text-gray-800 relative overflow-visible">
                        {/* Single vertical connector line (inspired by the warning list pattern) */}
                        <div className="absolute border-l-4 h-[84%] left-16 top-[6%] border-green-150 rounded" />

                        <div className="flex flex-row gap-4 relative">
                          <div className="absolute h-1 bg-green-150 top-[55%] -left-8 w-8"></div>
                          <span className="absolute z-10 flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-green-150 top-[45%] -left-2 bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-green-150"></span>
                          </span>
                          <div className="flex-1 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-green-50 border-green-150 py-3 px-6">
                              Cholesterol Levels
                            </span>
                          </div>
                          <div className="flex-1 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-green-50 border-green-150 py-3 px-6">
                              Diabetes Status
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-row gap-4 relative">
                          <div className="absolute h-1 bg-green-150 top-[55%] left-[-5%] w-8"></div>
                          <span className="absolute z-10 flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-green-150 top-[45%] -left-2 bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-green-150"></span>
                          </span>
                          <div className="flex-1 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-green-50 border-green-150 py-3 px-6">
                              Blood Pressure
                            </span>
                          </div>
                          <div className="flex-1 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-green-50 border-green-150 py-3 px-6">
                              Smoking History
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-row gap-4 relative">
                          <div className="absolute h-1 bg-green-150 top-[55%] left-[-5%] w-8"></div>
                          <span className="absolute z-10 flex justify-center items-center w-[17px] h-[17px] rounded-full border-[4px] border-green-150 top-[45%] -left-2 bg-white">
                            <span className="flex w-[7px] h-[7px] rounded-full bg-green-150"></span>
                          </span>
                          <div className="flex-1 min-w-0 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-green-50 border-green-150 py-3 px-6">
                              Family History
                            </span>
                          </div>
                          <div className="flex-1 min-w-0 relative">
                            <span className="flex w-full border-4 rounded-[12px] bg-green-50 border-green-150 py-3 px-6 whitespace-nowrap">
                              Past Heart/Stroke Events
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        default:
          return null;
      }
    }

    return null;
  };

  if (!step) {
    return null;
  }

  const importanceAlertProps = {
    open: showInfoDialog,
    className: "font-semibold text-gray-800",
    handleClose: () => setShowInfoDialog(false),
    onCross: () => setShowInfoDialog(false),
    confirmAction: () => {
      setShowInfoDialog(false);
      moveToNextTab();
    },
  };

  const ImportanceAlertComponent = isCholesterolTab
    ? CholesterolImportanceAlert
    : MedicationImportanceAlert;

  return (
    <>
      {step.type === EducationUITypes.InfoCard && (
        <div className="relative flex min-h-[490px] flex-col gap-3 p-3">
          <h1 className="h-[29px] font-bold text-2xl text-REELTeal">
            {step.title}
          </h1>
          <div className="pt-4 flex flex-row items-center gap-[20px]">
            {!isFirstPage && (
              <div className="absolute inset-y-0 left-0 z-10 flex items-center justify-center p-[10px]">
                <button
                  className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                  onClick={() => {
                    moveBack();
                  }}
                >
                  <ChevronLeftIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                </button>
              </div>
            )}
            <div className="flex flex-col items-end">
              <div className="justify-between">
                <InformationPage
                  cards={step.cards}
                  iconUrl={step.iconUrl}
                  type={step.type}
                />
              </div>
            </div>
            <div className="absolute inset-y-0 right-0 z-10 flex items-center justify-center p-[10px]">
              <button
                className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                onClick={() => {
                  !isLastPage ? goToNextPage() : confirmEducation();
                }}
              >
                <ChevronRightIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>
      )}
      {step.type === EducationUITypes.DrugTable && (
        <div className="relative flex min-h-[490px] flex-col gap-2 p-1">
          <h1 className="h-[29px] font-bold text-2xl text-teal-300">
            {step.title}
          </h1>
          <div className="pt-4 flex flex-row justify-between items-center gap-[20px]">
            {!isFirstPage && (
              <div className="absolute left-2 top-1/2 -translate-y-1/2 z-0 flex items-center justify-center">
                <button
                  className="flex items-center justify-center w-[48px] h-[48px] -ml-6 bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  onClick={() => {
                    moveBack();
                  }}
                >
                  <ChevronLeftIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                </button>
              </div>
            )}
            <div className="flex w-full flex-row -ml-3">
              <DrugTableUI
                saveDrugTableInfo={saveDrugTableInfo}
                onSelectDrugForMedicationCreate={onSelectDrugForMedicationCreate}
                theme={isCholesterolTab ? "cholesterol" : undefined}
                classes={
                  isCholesterolTab
                    ? CholesterolMedicationClasses
                    : isBloodPressureTab
                      ? BloodPressureMedicationClasses
                      : MedicationClasses
                }
              />
            </div>
            <div className="absolute -right-0 top-1/2 -translate-y-1/2 z-0 flex items-center justify-center">
              <button
                className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                onClick={() => {
                  // If on Cholesterol and no selected drugs, open Lifestyle dialog
                  if (
                    currentTabName?.toLowerCase() === "cholesterol" &&
                    (!amrEducation || amrEducation.length === 0)
                  ) {
                    setShowInfoDialog(true);
                  } else {
                    // setSaveDrugTableInfo(true);
                    // setTimeout(() => {
                    goToNextPage();
                    // }, 300);
                  }
                }}
              >
                <ChevronRightIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>
      )}
      {step.type === EducationUITypes.DrugCard && (
        <div className="relative flex min-h-[530px] w-full min-w-0 max-w-full flex-col gap-3 overflow-x-clip p-3">
          <div className="w-full bg-gray-20 py-3 px-2 mb-0 rounded-t-lg">
            <h1 className="font-bold text-2xl text-teal-300">
              {step.title}
            </h1>
          </div>
          <>
            <div className="relative flex min-w-0 flex-1 flex-row items-center">
              {/* <div className="flex flex-row justify-center items-center"> */}

              {!isFirstPage && (
                <div className="absolute left-2 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center">
                  <button
                    className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                    onClick={() => {
                      moveBack();
                    }}
                  >
                    <ChevronLeftIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                  </button>
                </div>
              )}

              <div className="min-w-0 flex-1 px-16">
                <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain scroll-smooth">
                  <div className={`inline-flex min-h-[390px] flex-row flex-nowrap items-start ${amrEducation?.length === 1
                      ? 'w-full justify-center'
                      : amrEducation?.length === 2
                        ? 'w-full justify-center gap-8'
                        : amrEducation?.length >= 3
                          ? 'w-max justify-start gap-8'
                          : 'w-full justify-start pl-8'
                    }`}>
                    <DrugCardUI
                      iconUrl={step.iconUrl}
                      currentTabName={currentTabName}
                      headerBg={isCholesterolTab ? "bg-green-75" : undefined}
                      borderClass={isCholesterolTab ? "border-green-150" : undefined}
                      headerTextClass={isCholesterolTab ? "text-teal-300" : undefined}
                      selectedBorderClass={isCholesterolTab ? "border-green-150" : undefined}
                    />
                  </div>
                </div>
              </div>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center">
                <button
                  className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                  onClick={() => {
                    !isLastPage ? goToNextPage() : goToNextTab();
                  }}
                >
                  <ChevronRightIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                </button>
              </div>
            </div>
          </>
        </div>
      )}
      {step.type === EducationUITypes.Custom && (
        <div className="relative flex min-h-[490px] flex-col gap-3 overflow-visible p-0">
          <div className="w-full bg-gray-20 py-3 px-2 mb-2 rounded-t-lg flex items-center justify-between">
            <h1 className="font-bold text-2xl text-teal-300">{step.title}</h1>
            {activePage === 2 && (
              <div className="flex items-center gap-2 text-gray-800 text-md font-normal relative z-50">
                {isBloodPressureTab && (
                  <button type="button" data-tooltip-id="blood-pressure-numbers-tooltip" className="flex gap-2 inline-flex disabled:opacity-50 rounded-[4px] transition-all duration-300 text-body-md items-center font-semibold justify-center px-6 py-3.5 border-REELTeal border text-REELTeal hover:text-REELTeal hover:bg-gray-50 !rounded-[30px]  p-1 !text-lg">
                    + My Blood Pressure Numbers <ReactSVG
                      src="/icons/information.svg"
                      className="w-5 h-5 text-bloodPressureColor"
                    /></button>
                )}
                {isCholesterolTab && (
                  <button type="button" data-tooltip-id="cholesterol-numbers-tooltip" className="flex gap-2 inline-flex disabled:opacity-50 rounded-[4px] transition-all duration-300 text-body-md items-center font-semibold justify-center px-6 py-3.5 border-green-300 border text-green-300 hover:text-green-600 hover:bg-gray-50 !rounded-[30px]  p-1 !text-lg">+ My Cholesterol Numbers <ReactSVG
                    src="/icons/information.svg"
                    className="w-5 h-5 text-cholesterolColor"
                  /></button>
                )}
                <NewToolTip
                  id="blood-pressure-numbers-tooltip"
                  render={() => (<div className="bg-gray-10 p-4 rounded shadow-lg max-w-md">
                    <p className="text-base text-gray-700">
                      Coming soon.
                    </p>
                  </div>)}
                />
                <NewToolTip
                  id="cholesterol-numbers-tooltip"
                  render={() => (<div className="bg-gray-10 p-4 rounded shadow-lg max-w-md">
                    <p className="text-base text-gray-700">
                      Coming soon.
                    </p>
                  </div>)}
                />


              </div>
            )}
          </div>
          <>
            <div className="flex flex-row justify-between items-center gap-[20px]">
              {/* <div className="flex flex-row justify-center items-center"> */}

              {!isFirstPage && (
                <div className="absolute inset-y-0 left-0 z-10 flex items-center justify-center p-[10px]">
                  <button
                    className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                    onClick={() => {
                      moveBack(activePage);
                    }}
                  >
                    <ChevronLeftIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                  </button>
                </div>
              )}
              <div className="flex w-full min-w-0 max-w-full flex-col">
                <div className="flex min-h-[310px] w-full min-w-0 max-w-full flex-row items-start justify-start pl-8">
                  {handleCustomContent(tabIndex, activePage)}
                </div>
                <div className="items-end justify-end flex float-right">
                  {isLastPage && (
                    <button
                      className={`absolute items-center justify-center bottom-0 right-0 py-2 px-4 gap-[10px] flex flex-row max-h-full max-w-full rounded-full bg-REELTeal hover:bg-teal-200`}
                      onClick={() => goToNextTab()}
                    >
                      <h3 className="text-body-md font-semibold text-white">
                        {getNextTabName()}
                      </h3>
                      <ChevronRightIcon strokeWidth={2.5} className="w-4 ml-2 h-4 text-white" />
                    </button>
                  )}
                </div>
              </div>
              {!isLastPage && (
                <div className="absolute inset-y-0 right-0 z-10 flex items-center justify-center p-[10px]">
                  <button
                    className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                    onClick={() => {
                      goToNextPage();
                    }}
                  >
                    <ChevronRightIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                  </button>
                </div>
              )}
            </div>
          </>
        </div>
      )}
      {step.type === EducationUITypes.CircleCard && (
        <div className="relative flex min-h-[490px] flex-col gap-3 p-3">
          <div className="w-full bg-gray-20 py-3 px-2 rounded-t-lg">
            <h1 className="font-bold text-2xl text-REELTeal">
              {step.title}
            </h1>
          </div>
          <div className="flex flex-row justify-between items-center gap-[20px]">
            {/* <div className="flex flex-row items-center justify-center"> */}

            {!isFirstPage && (
              <div className="absolute inset-y-0 left-0 z-10 flex items-center justify-center p-[10px]">
                <button
                  className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                  onClick={() => {
                    moveBack();
                  }}
                >
                  <ChevronLeftIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                </button>
              </div>
            )}
            <div className="flex w-full flex-col">
              <div className="flex min-h-[310px] flex-row pl-8">
                <InformationPage
                  cards={step.cards}
                  iconUrl={step.iconUrl}
                  type={step.type}
                />
              </div>
            </div>
            {!isLastPage && (
              <div className="absolute inset-y-0 right-0 z-10 flex items-center justify-center p-[10px]">
                <button
                  className="flex items-center justify-center w-[48px] h-[48px] bg-gray-20 rounded-full hover:bg-gray-100 transition-colors duration-200 text-teal-100"
                  onClick={() => {
                    goToNextPage();
                  }}
                >
                  <ChevronRightIcon stroke={chevronStrokeColor} strokeWidth={2.5} className="w-8 h-8" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {step.type === EducationUITypes.CommingSoon && (
        <div className="relative flex min-h-[490px] flex-col gap-3 p-3">
          <div className="w-full bg-gray-20 py-3 px-2 rounded-t-lg">
            <h1 className="font-bold text-2xl text-REELTeal">
              {step.title}
            </h1>
          </div>
          <div className="flex flex-row justify-between items-center gap-[20px]">
            <div className="flex flex-col w-full">
              <div className="items-end justify-end flex float-right">
                {isLastPage && (
                  <button
                    className={`absolute items-center justify-center bottom-0 right-0 py-2 px-4 gap-[10px] flex flex-row max-h-full max-w-full rounded-full bg-REELTeal hover:bg-teal-200`}
                    onClick={() => goToNextTab()}
                  >
                    <h3 className="text-body-md font-semibold text-white">
                      {getNextTabName()}
                    </h3>
                    <ArrowRightIcon className="w-4 ml-2 h-4 text-white" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ImportanceAlertComponent {...importanceAlertProps} />
      <InformationServiceModel
        open={showBlisterPackDialog}
        isDisabled={blisterPackOption === null}
        title="Blister Pack Option"
        content={() => (
          <>
            <div className="w-[387px] h-[70px] flex flex-col gap-2">
              <h3 className="flex items-center text-base font-semibold text-gray-600">
                Do you wish to have a Blister Pack prepared by your Pharmacy?
              </h3>
            </div>
            <div className="grid gap-4 grid-cols-1">
              <Button
                className={`min-w-fit ${blisterPackOption === "Yes" && "!bg-gray-100"
                  }`}
                type="primary-outline"
                text="Yes"
                onClick={() => {
                  setBlisterPackOption("Yes");
                }}
              />
              <Button
                className={`min-w-fit ${blisterPackOption === "No" && "!bg-gray-100"
                  }`}
                type="primary-outline"
                text="No"
                onClick={() => {
                  setBlisterPackOption("No");
                }}
              />
              <Button
                className={`min-w-fit ${blisterPackOption === "MaybeLater" && "!bg-gray-100"
                  }`}
                type="primary-outline"
                text="Maybe Later"
                onClick={() => {
                  setBlisterPackOption("MaybeLater");
                }}
              />
            </div>
          </>
        )}
        handleClose={() => {
          setShowBlisterPackDialog(false);
        }}
        onContinue={() => {
          setBlisterPackOption(null);
          setShowBlisterPackDialog(false);
          moveToNextTab();
        }}
      />
      {step.type === EducationUITypes.Custom && activePage === 2 && (
        <NewToolTip
          id={`bp-integration-tooltip-${activePage}`}
          place="top"
          render={() => (
            <div className="bg-gray-10 p-4 rounded shadow-lg max-w-md">
              <p className="text-sm text-gray-700">
                Coming soon.
              </p>
            </div>
          )}
        />
      )}
    </>
  );
};

export default EducationPage;
